import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Modal, Table } from "react-bootstrap";
import { Card, getCards } from "../../api/tooling";
import { dateLabel, ErrorNotice } from "./ToolingShared";

export default function DeletedCards({
  onClose,
  onView,
}: {
  onClose: () => void;
  onView: (card: Card) => void;
}) {
  const [search, setSearch] = useState("");
  const cards = useQuery({
    queryKey: ["tooling", "cards", "deleted"],
    queryFn: () => getCards(false, true),
  });
  const visible =
    cards.data?.filter((card) =>
      `${card.id} ${card.tool.code} ${card.tool.parts
        .map((part) => part.part_name)
        .join(" ")} ${card.detail} ${card.makers
        .map((maker) => maker.name)
        .join(" ")}`
        .toLowerCase()
        .includes(search.toLowerCase())
    ) || [];
  return (
    <Modal
      show
      onHide={onClose}
      size="xl"
      scrollable
      aria-labelledby="deleted-cards-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="deleted-cards-title">Deleted cards</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorNotice error={cards.error} retry={() => cards.refetch()} />
        <input
          className="form-control mb-3"
          aria-label="Search deleted cards"
          placeholder="Search card, tool, part, maker or details…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <p className="text-muted small">
          Deleted cards are kept for reference and do not contribute points.
        </p>
        {cards.isLoading ? (
          <p role="status">Loading deleted cards…</p>
        ) : (
          <Table responsive striped className="tooling-deleted-table">
            <thead>
              <tr>
                <th>Card</th>
                <th>Tool / parts</th>
                <th>Details</th>
                <th>Die makers</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((card) => (
                <tr key={card.id}>
                  <td>
                    #{card.id}
                    <br />
                    <span className="tooling-record-status is-deleted">
                      Deleted
                    </span>
                  </td>
                  <td>
                    {card.tool.code}
                    <br />
                    <small>
                      {card.tool.parts.map((part) => part.part_name).join(", ")}
                    </small>
                  </td>
                  <td>
                    <p className="tooling-deleted-preview">{card.detail}</p>
                  </td>
                  <td>{card.makers.map((maker) => maker.name).join(", ")}</td>
                  <td>{dateLabel(card.created_at)}</td>
                  <td>
                    <Button
                      size="sm"
                      className="tooling-audit-button"
                      onClick={() => onView(card)}
                    >
                      View card
                    </Button>
                  </td>
                </tr>
              ))}
              {!cards.isError && visible.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    {search
                      ? "No matching deleted cards."
                      : "No deleted cards."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
